<template>
  <div v-if="!isNativeMobile" class="anzera-chatbot-container">
    <!-- Launcher butonu - header içinde kalır -->
    <div class="anzera-chatbot-launcher">
      <button aria-label="Anzera Power"
              class="chatbot-button"
              type="button"
              @click="onLauncherClick"
      >
        <img :src="chatIcon" class="chatbot-button-img" draggable="false"/>
      </button>

      <transition name="fade">
        <div v-if="showHello && !isOpen" class="chatbot-hello">
          <div class="chatbot-hello-body">
            <p class="chatbot-hello-text">
              {{ $t('general.hello_word') }}! <br/> {{ $t('general.can_i_help_you') }}
            </p>
          </div>
          <div class="chatbot-hello-actions">
            <button class="chatbot-cta"
                    @click="
                showHello = false;
                toggleChatbot();
              "
            >
              {{ $t('general.yes_sure') }}
            </button>
            <button aria-label="Kapat"
                    class="chatbot-x"
                    @click="closeHello"
            >
              ×
            </button>
          </div>
        </div>
      </transition>
    </div>
    <!-- Panel - teleport container, mounted'da body'ye taşınır -->
    <div ref="panelContainer" class="chatbot-panel-teleport">
      <div v-if="isOpen" aria-modal="false" class="chatbot-panel" role="dialog">
      <div class="chatbot-header-container">
        <div class="chatbot-header">
        <div class="chatbot-title">
          <img :src="headIcon" alt="Asistan" class="chatbot-head"/>
          <div class="chatbot-name">
            <p class="chatbot-name-text">Anzera Power</p>
            <div class="chatbot-presence">
              <span :class="['point', presence === 'online' ? 'on' : 'off']"/>
              <span>{{ $t(presence === "online" ? "general.online" : "general.offline") }}</span>
            </div>
          </div>
        </div>
        <button class="chatbot-close" @click="toggleChatbot">×</button>
        </div>
        <div v-if="showModeButtons" class="chatbot-mode-buttons">
          <button
            :class="['mode-btn', 'mode-btn-report', { 'active': !aiConversationId }]"
            @click="switchToReportMode"
            title="Rapor Modu"
          >
           <i class="uil-file-alt"></i>
          </button>
          <button
            :class="['mode-btn', 'mode-btn-support', { 'active': aiConversationId }]"
            @click="switchToSupportMode"
            title="Teknik Destek"
          >
            <i class="uil-wrench"></i>
          </button>
        </div>
      </div>

      <div ref="messageArea" class="chatbot-body">
        <div class="msg bot">
          <img :src="headIcon" alt="" class="chatbot-head"/>
          <div class="msg-text bot-bubble">
            <p>{{ $t('general.chatbot_welcome_message') }}</p>
            <div :class="{ 'action-disabled': !canClick }"
                 :inert="!canClick ? '' : null"
                 class="quick"
            >
              <button :disabled="!canClick"
                      @click="sendInitialRequest('report')"
              >
                {{ $t('general.i_want_report') }}
              </button>
              <button :disabled="!canClick"
                      @click="sendInitialRequest('support')"
              >
                {{ $t('general.i_need_technical_support') }}
              </button>
            </div>
          </div>
        </div>

        <div v-for="(message, ind) in messages"
             :key="message.id"
             :class="{
            user: message.sender === 'user',
            bot: message.sender !== 'user',
          }"
             class="msg"
        >
          <template v-if="message.sender === 'user'">
            <div class="msg-user-text user-bubble">
              {{ message.content.message }}
            </div>
            <UserProfileAvatar
              :name="currentUserName"
              :photo="currentUserPhoto"
              class="chatbot-user-avatar"
            />
          </template>

          <template v-else>
            <img :src="headIcon" alt="" class="chatbot-head"/>
            <div class="msg-text bot-bubble">
              <div v-if="message.content.type === 'text'">
                <p class="ai-message-text">{{ message.content.message }}</p>
              </div>

              <div v-else-if="message.content.type === 'confirmation'"
                   :class="{
                  'action-disabled': ind !== lastInteractiveIndex || !canClick,
                }"
                   :inert="ind !== lastInteractiveIndex || !canClick ? '' : null"
                   class="action-wrapper"
              >
                <p class="ai-message-text">
                  <strong>{{ message.content.message }}</strong>
                </p>

                <div v-if="message.content.components.details"
                     class="details-container"
                >
                  <div v-for="(value, key) in message.content.components.details"
                       :key="key"
                       class="detail-item"
                  >
                    <p>{{ key }}: {{ value }}</p>
                  </div>
                </div>

                <div class="action-buttons-container quick">
                  <button v-for="action in message.content.components.actions"
                          :key="action"
                          class="action-button"
                          @click="sendAction(action)"
                  >
                    {{ getActionLabel(action) }}
                  </button>
                </div>
              </div>

              <div v-else-if="message.content.type === 'interactive'"
                   :class="{
                  'action-disabled': ind !== lastInteractiveIndex || !canClick,
                }"
                   :inert="ind !== lastInteractiveIndex || !canClick ? '' : null"
                   class="action-wrapper"
              >
                <p class="ai-message-text">{{ message.content.message }}</p>

                <div v-if="message.content.components.type === 'checkbox_list'"
                     class="checkbox-list-container"
                >
                  <div class="checkbox-item select-all-item">
                    <input :id="'select-all-' + message.id"
                           v-model="selectAll"
                           type="checkbox"
                           @change="toggleSelectAll(message)"
                    />
                    <label :for="'select-all-' + message.id">
                      <strong>{{ $t('general.select_all') }}</strong>
                    </label>
                  </div>
                  <div v-for="option in message.content.components.options"
                       :key="option.value"
                       class="checkbox-item"
                  >
                    <input :id="'item-' + option.value"
                           v-model="currentSelections"
                           :value="option.value"
                           type="checkbox"
                           @change="
                        onCheckboxChange(option.value, $event.target.checked)
                      "
                    />
                    <label :for="'item-' + option.value">
                      <strong>{{ option.label }}</strong>
                      <small v-if="option.description">{{
                          option.description
                        }}</small>
                    </label>
                  </div>

                  
                </div>

                <div v-else-if="message.content.components.type === 'button_group'"
                     class="button-group-container quick"
                >
                  <button v-for="button in message.content.components.options"
                          :key="button.id"
                          class="action-button"
                          @click="sendButtonAction(button)"
                  >
                    {{ button.icon }} {{ button.label }}
                  </button>
                </div>
                <div class="action-buttons-wrapper">
                    <button :disabled="currentSelections.length === 0"
                            class="action-button send-selection-button"
                            @click="sendSelection"
                    >
                      {{ $t('general.confirm') }}
                    </button>
                    <button class="action-button cancel-selection-button"
                            @click="cancelReportSelection"
                    >
                      {{ $t('general.cancel') }}
                    </button>
                  </div>
              </div>
            </div>
          </template>
        </div>

        <div v-if="showTypingIndicator"
             aria-live="polite"
             class="msg bot msg-typing"
        >
          <img :src="headIcon" alt="" class="chatbot-head"/>
          <div class="msg-text bot-bubble typing-bubble">
            <div class="typing-dots" aria-hidden="true">
              <i class="typing-dot"></i>
              <i class="typing-dot"></i>
              <i class="typing-dot"></i>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isInputVisible" class="chatbot-input">
        <input v-model="currentMessage"
               :disabled="!canClick"
               :placeholder="canClick ? $t('general.type_your_message') : $t('general.preparing') + '...'"
               class="chatbot-editor chatbot-editor-input"
               @keyup.enter="sendMessage(null, currentMessage)"
        />
        <button :disabled="!canClick"
                aria-label="Gönder"
                class="chatbot-send-btn"
                title="Gönder"
                @click="sendMessage(null, currentMessage)"
        >
          <i class="uil-telegram-alt"></i>
        </button>
      </div>
    </div>
    </div><!-- /chatbot-panel-teleport -->
  </div>
</template>
<script>
import store from "@/core/services";
import SecureLS from "secure-ls";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import i18nService from "@/core/services/i18n.service";
import { externalAiHttpClient } from "@/core/services/api.service";
import chatbotModule, {
  MODULE_NAME as CHATBOT_MODULE,
  AI_START_SESSION,
  AI_LOAD_HISTORY,
  AI_SEND_MESSAGE,
  STATUS,
} from "@/core/services/store/anzeraChatbot.module";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
const LS_SESSION_KEY = "chatbot_session_id";
const LS_HELLO_CLOSED_KEY = "chatbot_hello_closed_at";

export default {
  name: "AnzeraChatBot",
  beforeCreate() {
    if (!(store && store.state && store.state[CHATBOT_MODULE])) {
      store.registerModule(CHATBOT_MODULE, chatbotModule);
    }
  },
  components: { UserProfileAvatar },
  props: { userImage: { type: String, default: "" } },

  data() {
    return {
      isOpen: false,
      isNativeMobile: typeof window !== "undefined" && !!window.ReactNativeWebView,
      sessionId: null,
      messages: [],
      currentMessage: "",
      currentSelections: [],
      selectAll: false,
      showHello: true,
      chatIcon:
          process.env.BASE_URL + "media/images/chatbot/apower-chat-icon.png",
      headIcon:
          process.env.BASE_URL + "media/images/chatbot/apower-head-icon.png",
      singleSelectOnly: false,

      pageSize: 10,
      hasMoreOld: true,
      isLoadingPage: false,
      _scrollBound: false,
      _onScroll: null,

      isGetting: false,

      // AI Assistant
      aiJwtToken: null,
      aiConversationId: null,
      aiAssistantId: parseInt(process.env.VUE_APP_AI_ASSISTANT_ID) || 16,
      aiApiKeyName: process.env.VUE_APP_AI_JWT_API_KEY || '',
      isAiTyping: false,
      isBotTyping: false,
    };
  },

  computed: {
    isInputVisible() {
      const last = this.messages[this.messages.length - 1];
      if (!last || last.sender === "user") return true;
      return (last.content || {}).type === "text";
    },
    presence() {
      return store.getters?.[`${CHATBOT_MODULE}/${STATUS}`] || "online";
    },
    canClick() {
      return !!this.sessionId && !this.isGetting;
    },
    lastInteractiveIndex() {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const m = this.messages[i];
        const t = m?.content?.type;
        if (m.sender === "ai" && (t === "interactive" || t === "confirmation"))
          return i;
      }
      return -1;
    },
    showModeButtons() {
      return this.messages.length > 2;
    },
    showTypingIndicator() {
      return this.isAiTyping || this.isBotTyping;
    },
    currentUser() {
      try {
        const storedUser = ls.get("user");
        return typeof storedUser === "string" ? JSON.parse(storedUser) : storedUser;
      } catch (e) {
        return null;
      }
    },
    currentUserName() {
      const user = this.currentUser;
      return (
        [user?.name, user?.surname].filter(Boolean).join(" ") ||
        user?.fullname ||
        ""
      );
    },
    currentUserPhoto() {
      return this.userImage || this.currentUser?.photo || this.currentUser?.photo_link || "";
    },
  },

  created() {
    const saved = localStorage.getItem(LS_SESSION_KEY);
    if (saved) this.sessionId = saved;

    // AI token ve conversation
    try {
      const savedAiToken = ls.get("aiJwtToken");
      const savedAiConversation = ls.get("aiConversationId");
      if (savedAiToken) this.aiJwtToken = savedAiToken;
      if (savedAiConversation) this.aiConversationId = savedAiConversation;
    } catch (e) {
    }

    this.showHello = this.shouldShowHello();
  },

  mounted() {
    // Panel container'ı body'ye taşı (bir kere) - böylece header transform'larından etkilenmez
    const container = this.$refs.panelContainer;
    if (container) {
      document.body.appendChild(container);
    }
  },

  beforeDestroy() {
    this._unbindScrollForOlder();
    // Panel container body'de ise temizle
    const container = this.$refs.panelContainer;
    if (container && container.parentNode === document.body) {
      document.body.removeChild(container);
    }
  },
  methods: {
    shouldShowHello() {
      try {
        const closedAt = ls.get(LS_HELLO_CLOSED_KEY);
        return !closedAt;
      } catch (e) {
        return true;
      }
    },
    closeHello() {
      this.showHello = false;
      try {
        ls.set(LS_HELLO_CLOSED_KEY, new Date().toISOString());
      } catch (e) {
      }
    },

    switchToReportMode() {
      if (this.aiConversationId) {
        this.aiConversationId = null;
        this.aiJwtToken = null;
        try {
          ls.remove('aiConversationId');
          ls.remove('aiJwtToken');
        } catch (e) {}
      }

      this.messages = [];

      const message = this.$t('general.i_want_report');
      this.messages.push({
        id: Date.now(),
        sender: "user",
        content: { message },
        timestamp: new Date().toISOString(),
      });

      this._postToApi(message);
    },

    switchToSupportMode() {
      if (!this.aiConversationId) {
        this.messages = [];

        const message = this.$t('general.i_need_technical_support');
        this.messages.push({
          id: Date.now(),
          sender: "user",
          content: { message },
          timestamp: new Date().toISOString(),
        });

        this.isAiTyping = true;
        this._initAiChat()
          .catch(() => {
            this.messages.push({
              id: Date.now() + 1,
              sender: "ai",
              content: {
                type: "text",
                message: this.$t("general.ai_chat_error")
              },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => {
            this.isAiTyping = false;
            this.scrollToBottom();
          });
      }
    },
    toggleChatbot() {
      this.isOpen = !this.isOpen;

      if (!this.isOpen) {
        store.commit(`${CHATBOT_MODULE}/M/STATUS`, "offline");

        this.messages = [];

        this.aiConversationId = null;
        this.aiJwtToken = null;

        try {
          ls.remove('aiConversationId');
          ls.remove('aiJwtToken');
        } catch (e) {
        }

        return;
      }

      store.commit(`${CHATBOT_MODULE}/M/STATUS`, "online");

      this.isGetting = true;
      const boot = this.sessionId ? Promise.resolve() : this.startSession();
      boot
          .finally(() => {
            this.isGetting = false;
          });
    },

    onLauncherClick() {
      this.toggleChatbot();
    },

    sendInitialRequest(type) {
      const messageKey = type === 'report' ? 'general.i_want_report' : 'general.i_need_technical_support';
      const message = this.$t(messageKey);

      if (type === 'support') {
        this.messages.push({
          id: Date.now(),
          sender: "user",
          content: { message },
          timestamp: new Date().toISOString(),
        });

        this.isAiTyping = true;
        this._initAiChat()
          .catch(() => {
            this.messages.push({
              id: Date.now() + 1,
              sender: "ai",
              content: {
                type: "text",
                message: this.$t("general.ai_chat_error")
              },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => {
            this.isAiTyping = false;
            this.scrollToBottom();
          });
      } else {
        this.sendMessage(null, message);
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const area = this.$refs.messageArea;
        if (area) area.scrollTop = area.scrollHeight;
      });
    },

    getAuthHeaders() {
      let token = null,
          company = null;
      try {
        token = ls.get("token");
      } catch {
      }
      try {
        const u = ls.get("user");
        const parsed = typeof u === "string" ? JSON.parse(u) : u;
        company = parsed?.company_id || parsed?.company?.id || null;
      } catch {
      }
      if (!token)
        token =
            localStorage.getItem("token") || localStorage.getItem("access_token");
      if (!company) {
        try {
          const u2 = JSON.parse(localStorage.getItem("user") || "null");
          company = u2?.company_id || u2?.company?.id || null;
        } catch {
        }
      }
      if (!company) company = localStorage.getItem("company_id");
      const headers = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      if (company) {
        headers.Company = String(company);
        headers["X-Company-Id"] = String(company);
      }
      return { headers, token, company };
    },

    buildAbsUrl(pathOrAbs) {
      if (typeof pathOrAbs !== "string") return "";
      if (/^https?:\/\//i.test(pathOrAbs)) return pathOrAbs.trim();
      const base = (process.env.VUE_APP_BACKEND_APP_URL || "").replace(
          /\/+$/g,
          ""
      );
      const path = String(pathOrAbs || "").replace(/^\/+/, "");
      return `${base}/${path}`;
    },

    startSession() {
      return store
          .dispatch(`${CHATBOT_MODULE}/${AI_START_SESSION}`)
          .then((res) => {
            this.sessionId = res?.data?.session_id || null;
            if (this.sessionId) {
              localStorage.setItem(LS_SESSION_KEY, this.sessionId);
              store.commit(`${CHATBOT_MODULE}/M/STATUS`, "online");
            }
          })
          .catch(() => {});
    },

    _mapServerItemsAsc(items) {
      let lastCtx = null;
      return items.map((msg) => {
        if (msg.role === "assistant") {
          const type =
              msg.message_type || (msg.components?.type ? "interactive" : "text");
          const ai = {
            id: msg.id,
            sender: "ai",
            content: {
              type,
              message: msg.content || "",
              components: msg.components || null,
            },
            timestamp: msg.created_at,
          };
          lastCtx = ai.content?.components?.type
              ? ai.content.components
              : ai.content?.type === "confirmation"
                  ? {
                    type: "confirmation",
                    actions: ai.content?.components?.actions || [],
                  }
                  : null;
          return ai;
        } else {
          const pretty = this.resolveUserReply(msg.content, lastCtx);
          lastCtx = null;
          return {
            id: msg.id,
            sender: "user",
            content: { message: pretty },
            timestamp: msg.created_at,
          };
        }
      });
    },

    resolveUserReply(raw, lastCtx) {
      if (!raw) return "";
      if (!lastCtx) return String(raw);
      const txt = String(raw).trim();
      if (lastCtx.type === "checkbox_list") {
        const map = new Map(
            (lastCtx.options || []).map((o) => [
              String(o.value),
              o.label || String(o.value),
            ])
        );
        return txt
            .split(",")
            .map((s) => map.get(s.trim()) || s.trim())
            .join(", ");
      }
      if (lastCtx.type === "button_group") {
        const hit = (lastCtx.options || []).find(
            (o) => String(o.id) === txt || String(o.value) === txt
        );
        return hit?.label || txt;
      }
      if (lastCtx.type === "confirmation") {
        const t = txt.toLowerCase();
        if (["evet", "confirm", "onay", "onayla"].includes(t))
          return this.getActionLabel("confirm");
        if (["iptal", "cancel"].includes(t))
          return this.getActionLabel("cancel");
        if (["düzenle", "duzenle", "edit"].includes(t))
          return this.getActionLabel("edit");
        return txt;
      }
      return txt;
    },

    sendMessage(_e, messageContent) {
      const text = (messageContent || this.currentMessage || "").trim();
      if (!text) return;

      const lowerText = text.toLowerCase();

      if ((/(teknik destek|technical support|yardım|help)/i.test(lowerText)) && !this.aiConversationId) {
        this.messages.push({
          id: Date.now(),
          sender: "user",
          content: { message: text },
          timestamp: new Date().toISOString(),
        });
        this.currentMessage = "";

        this.isAiTyping = true;
        this._initAiChat()
          .catch(() => {
            this.messages.push({
              id: Date.now() + 1,
              sender: "ai",
              content: {
                type: "text",
                message: this.$t("general.ai_chat_error") || "Teknik destek servisine bağlanırken bir hata oluştu."
              },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => {
            this.isAiTyping = false;
            this.scrollToBottom();
          });
        return;
      }

      if ((/rapor( almak istiyorum| moduna geç| moduna geçmek istiyorum| iste| istiyorum)|report( moduna geç| mode| request| want)/i.test(lowerText)) && this.aiConversationId) {
        this.aiConversationId = null;
        try {
          ls.remove('aiConversationId');
        } catch (e) {
        }

        this.messages.push({
          id: Date.now(),
          sender: "user",
          content: { message: text },
          timestamp: new Date().toISOString(),
        });
        this.currentMessage = "";

        if (!this.sessionId) {
          this.startSession().then(() => {
            this._postToApi(text);
          });
        } else {
          this._postToApi(text);
        }
        return;
      }

      if (this.aiConversationId) {
        this.messages.push({
          id: Date.now(),
          sender: "user",
          content: { message: text },
          timestamp: new Date().toISOString(),
        });
        this.currentMessage = "";

        this._sendAiMessage(text)
          .catch((err) => {
            this.messages.push({
              id: Date.now() + 1,
              sender: "ai",
              content: {
                type: "text",
                message: err.message || "Bir hata oluştu"
              },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => this.scrollToBottom());
        return;
      }

      if (!this.sessionId) return;
      this.singleSelectOnly = /anket raporu/i.test(text);

      this.messages.push({
        id: Date.now(),
        sender: "user",
        content: { message: text },
        timestamp: new Date().toISOString(),
      });
      this.currentMessage = "";
      this.isBotTyping = true;
      this.scrollToBottom();

      store
          .dispatch(`${CHATBOT_MODULE}/${AI_SEND_MESSAGE}`, {
            sessionId: this.sessionId,
            message: text,
          })
          .then(({ data }) => this._handleAiResponse(data))
          .catch((err) => {
            const data = err?.response?.data ?? err;
            const msg = typeof data === "string" ? data : data?.message;
            const type = data?.type || "text";
            this.messages.push({
              id: Date.now() + 1,
              sender: "ai",
              content: { type, message: msg },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => {
            this.isBotTyping = false;
            this.scrollToBottom();
          });
    },

    _postToApi(messageText) {
      this.isBotTyping = true;
      this.scrollToBottom();

      store
          .dispatch(`${CHATBOT_MODULE}/${AI_SEND_MESSAGE}`, {
            sessionId: this.sessionId,
            message: messageText,
          })
          .then(({ data }) => this._handleAiResponse(data))
          .catch((err) => {
            const data = err?.response?.data ?? err;
            const msg = typeof data === "string" ? data : data?.message;
            const type = data?.type || "text";
            this.messages.push({
              id: Date.now() + 2,
              sender: "ai",
              content: { type, message: msg },
              timestamp: new Date().toISOString(),
            });
          })
          .finally(() => {
            this.isBotTyping = false;
            this.scrollToBottom();
          });
    },

    _handleAiResponse(payload) {
      const aiPayload =
          typeof payload === "string" ? JSON.parse(payload) : payload;
      this.messages.push({
        id: Date.now() + 1,
        sender: "ai",
        content: aiPayload,
        timestamp: new Date().toISOString(),
      });

      const token =
          aiPayload?.components?.download_token || aiPayload?.download_token;
      if (token) return this.downloadReport({ token });

      const url = aiPayload?.download_url;
      if (url) return this.downloadReport({ url });

      if (aiPayload?.type === "text" && aiPayload?.message) {
        const m = String(aiPayload.message).match(
            /https?:\/\/\S+\/api\/ai\/report\/download\/[A-Za-z0-9_-]+/
        );
        if (m && m[0]) this.downloadReport({ url: m[0] });
      }
    },

    sendSelection() {
      if (this.singleSelectOnly && this.currentSelections.length > 1) {
        this.currentSelections = this.currentSelections.slice(-1);
      }
      const lastInteractive = [...this.messages]
          .reverse()
          .find(
              (m) =>
                  m.sender === "ai" &&
                  m.content?.type === "interactive" &&
                  m.content?.components?.type === "checkbox_list"
          );

      const allOptions = lastInteractive?.content?.components?.options || [];
      const allCount = allOptions.length;
      const selectedCount = this.currentSelections.length;

      let userMessage;
      let messageToSend;

      if (selectedCount === allCount && allCount > 0) {
        userMessage = this.$t('general.select_all');
        messageToSend = this.currentSelections.join(",");
      } else {
        const labelMap = new Map(
            allOptions.map((opt) => [
              String(opt.value),
              opt.label || String(opt.value),
            ])
        );
        const labels = this.currentSelections.map(
            (v) => labelMap.get(String(v)) || String(v)
        );
        userMessage = labels.join(", ");
        messageToSend = this.currentSelections.join(",");
      }

      this.messages.push({
        id: Date.now(),
        sender: "user",
        content: { message: userMessage },
        timestamp: new Date().toISOString(),
      });

      this.currentSelections = [];
      this._postToApi(messageToSend);
    },

    sendAction(action) {
      const userEcho = this.getActionLabel(action);
      this.messages.push({
        id: Date.now(),
        sender: "user",
        content: { message: userEcho },
        timestamp: new Date().toISOString(),
      });
      const msg =
          action === "confirm"
              ? "evet"
              : action === "edit"
                  ? "düzenle"
                  : action === "cancel"
                      ? "iptal"
                      : action;
      this._postToApi(msg);
    },

    sendButtonAction(btn) {
      const label = btn.label || btn.id;
      this.messages.push({
        id: Date.now(),
        sender: "user",
        content: { message: label },
        timestamp: new Date().toISOString(),
      });
      const msg = String(btn.id || btn.value || "").trim();
      if (msg) this._postToApi(msg);
    },

    getActionLabel(action) {
      return this.$t(`general.${action}`);
    },

    onCheckboxChange(_value, _checked) {
      this.updateSelectAllState();
    },

    toggleSelectAll(message) {
      const allValues = message.content.components.options.map(opt => opt.value);
      if (this.selectAll) {
        this.currentSelections = [...allValues];
      } else {
        this.currentSelections = [];
      }
    },

    updateSelectAllState() {
      const lastInteractive = [...this.messages]
        .reverse()
        .find(
          (m) =>
            m.sender === "ai" &&
            m.content?.type === "interactive" &&
            m.content?.components?.type === "checkbox_list"
        );
      if (!lastInteractive) {
        this.selectAll = false;
        return;
      }
      const allValues = lastInteractive.content.components.options.map(opt => opt.value);
      this.selectAll = allValues.length > 0 &&
        allValues.every(val => this.currentSelections.includes(val));
    },

    cancelReportSelection() {
      this.currentSelections = [];
      this._postToApi("iptal");
    },

    pickFileName(resp, base) {
      const cd =
          resp?.headers?.["content-disposition"] ||
          resp?.headers?.["Content-Disposition"];
      if (cd) {
        const m = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(cd);
        if (m) {
          const raw = m[1] || m[2];
          try {
            return decodeURIComponent(raw);
          } catch {
            return raw;
          }
        }
      }
      const ts = new Date()
          .toISOString()
          .replace(/[-:TZ.]/g, "")
          .slice(0, 14);
      const safe = String(base || "Anzera Raporu")
          .replace(/[^\w\s-]/g, "")
          .trim()
          .replace(/\s+/g, "_");
      return `${safe}_${ts}.xlsx`;
    },

    downloadReport({ token, url }) {
      const abs = token
          ? this.buildAbsUrl(`api/ai/report/download/${token}`)
          : this.buildAbsUrl(url);
      this._doDownload(abs);
    },

      _doDownload(url) {
          const a = document.createElement("a");
          a.href = url;
          a.download = this.downloadNameBase
              ? `${this.downloadNameBase}.xlsx`
              : "rapor.xlsx";
          document.body.appendChild(a);
          a.click();
          a.remove();
          this.scrollToBottom();
      },

    _fetchPage({ per_page, direction, cursor }) {
      return store
          .dispatch(`${CHATBOT_MODULE}/${AI_LOAD_HISTORY}`, {
            sessionId: this.sessionId,
            params: { per_page, direction, cursor },
          })
          .then(({ data }) => {
            const raw = Array.isArray(data?.items)
                ? data.items
                : Array.isArray(data)
                    ? data
                    : [];
            const asc = raw.slice().sort((a, b) => (a.id > b.id ? 1 : -1));
            return { mapped: this._mapServerItemsAsc(asc), rawLen: raw.length };
          });
    },

    loadInitialMessages() {
      if (!this.sessionId) return;
      return this._fetchPage({ per_page: this.pageSize, direction: "desc" })
          .then(({ mapped, rawLen }) => {
            this.messages = mapped;
            this.hasMoreOld = rawLen === this.pageSize;
            store.commit(`${CHATBOT_MODULE}/M/STATUS`, "online");
          })
          .catch((err) => {
            if (err?.response?.status === 404) {
              localStorage.removeItem(LS_SESSION_KEY);
              this.sessionId = null;
              return this.startSession().then(() => this.loadInitialMessages());
            }
          })
          .finally(() => {
            this.$nextTick(() => {
              this.scrollToBottom();
              this._bindScrollForOlder();
            });
          });
    },

    loadOlderMessages() {
      if (!this.sessionId || this.isLoadingPage || !this.hasMoreOld) return;
      const area = this.$refs.messageArea;
      const prevHeight = area ? area.scrollHeight : 0;

      const oldest = this.messages[0];
      const cursor = oldest ? oldest.id : null;
      if (!cursor) return;

      this.isLoadingPage = true;

      this._fetchPage({ per_page: this.pageSize, direction: "desc", cursor })
          .then(({ mapped, rawLen }) => {
            if (!rawLen) {
              this.hasMoreOld = false;
              return;
            }
            const existingIds = new Set(this.messages.map((m) => m.id));
            const dedup = mapped.filter((m) => !existingIds.has(m.id));
            this.messages = [...dedup, ...this.messages];

            this.$nextTick(() => {
              if (area) {
                const newHeight = area.scrollHeight;
                area.scrollTop = newHeight - prevHeight;
              }
            });

            this.hasMoreOld = rawLen === this.pageSize;
          })
          .catch((err) => {
            if (err?.response?.status === 404) {
              localStorage.removeItem(LS_SESSION_KEY);
              this.sessionId = null;
              return this.startSession().then(() => {
                this.messages = [];
                this.hasMoreOld = true;
                return this.loadInitialMessages();
              });
            }
          })
          .finally(() => {
            this.isLoadingPage = false;
          });
    },

    _bindScrollForOlder() {
      if (this._scrollBound) return;
      const area = this.$refs.messageArea;
      if (!area) return;

      this._onScroll = () => {
        if (area.scrollTop <= 24) this.loadOlderMessages();
      };
      area.addEventListener("scroll", this._onScroll);
      this._scrollBound = true;
    },

    _unbindScrollForOlder() {
      const area = this.$refs.messageArea;
      if (area && this._onScroll)
        area.removeEventListener("scroll", this._onScroll);
      this._scrollBound = false;
      this._onScroll = null;
    },

    // AI Assistant
    _initAiChat() {
      this.isGetting = true;
      return this._fetchAiJwtToken()
        .then(() => this._startAiConversation())
        .finally(() => {
          this.isGetting = false;
        });
    },

    _fetchAiJwtToken() {
      const userIdentifier = this._getAiUserIdentifier();
      const apiKey = this.aiApiKeyName;
      const lang = this._getAiLanguage();

      if (!apiKey) {
        return Promise.reject(new Error("API Key eksik"));
      }

      return externalAiHttpClient
        .post("/auth/token", {
          api_key: apiKey,
          user_identifier: userIdentifier,
          lang,
        })
        .then(({ data }) => data)
        .catch((error) => {
          throw new Error(
            error.response?.data?.message || error.message || "JWT alınamadı"
          );
        })
        .then((data) => {
          this.aiJwtToken = data.data?.token || data.token;
          if (!this.aiJwtToken) {
            throw new Error("Token response bulunamadı");
          }
          try {
            ls.set("aiJwtToken", this.aiJwtToken);
          } catch (e) {
          }
          return this.aiJwtToken;
        });
    },

    _startAiConversation() {
      if (!this.aiJwtToken) {
        return Promise.reject(new Error("JWT token eksik"));
      }
      const lang = this._getAiLanguage();

      const startConversation = (allowRefresh = true) => {
        return externalAiHttpClient
          .post(
            "/chat/conversations",
            {
              assistant_id: this.aiAssistantId,
              lang,
              metadata: {
                lang,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${this.aiJwtToken}`,
              },
            }
          )
          .catch((error) => {
            if (error.response?.status === 401 && allowRefresh) {
              return this._refreshAiToken().then(() =>
                startConversation(false)
              );
            }
            throw new Error(
              error.response?.data?.message ||
                error.message ||
                "Conversation oluşturulamadı"
            );
          });
      };

      return startConversation()
        .then(({ data }) => data)
        .then((data) => {
          this.aiConversationId = data.data?.id || data.data?.conversation_id || data.id || data.conversation_id;
          if (!this.aiConversationId) {
            throw new Error("Conversation ID bulunamadı");
          }
          try {
            ls.set("aiConversationId", this.aiConversationId);
          } catch (e) {
          }

          const messages = data.data?.messages || [];
          if (messages.length > 0) {
            messages.forEach(msg => {
              if (msg.role === 'assistant' && msg.content) {
                this.messages.push({
                  id: msg.id || Date.now(),
                  sender: "ai",
                  content: {
                    type: "text",
                    message: msg.content,
                  },
                  timestamp: new Date().toISOString(),
                });
              }
            });
            this.scrollToBottom();
          }

          return this.aiConversationId;
        });
    },

    _sendAiMessage(message) {
      if (!this.aiConversationId || !this.aiJwtToken) {
        return Promise.reject(new Error("AI hazır değil"));
      }

      const lang = this._getAiLanguage();
      this.isAiTyping = true;
      this.scrollToBottom();

      const sendMessage = (allowRefresh = true) => {
        return externalAiHttpClient
          .post(
            "/chat/messages",
            {
              conversation_id: this.aiConversationId,
              message: message,
              lang,
              metadata: {
                lang,
              },
            },
            {
              headers: {
                Authorization: `Bearer ${this.aiJwtToken}`,
              },
            }
          )
          .catch((error) => {
            if (error.response?.status === 401 && allowRefresh) {
              return this._refreshAiToken().then(() => sendMessage(false));
            }
            if (error.response?.status === 429) {
              this.isAiTyping = false;
              throw new Error(this.$t("general.rate_limit_message"));
            }
            this.isAiTyping = false;
            throw new Error(
              error.response?.data?.message ||
                error.message ||
                "Mesaj gönderilemedi"
            );
          });
      };

      return sendMessage()
        .then(({ data }) => data)
        .then((data) => {
          this._handleAiMessageResponse(data);
          return data;
        })
        .finally(() => {
          this.isAiTyping = false;
        });
    },

    _refreshAiToken() {
      return this._fetchAiJwtToken();
    },

    _getAiUserIdentifier() {
      try {
        const user = ls.get("user");
        const parsed = typeof user === "string" ? JSON.parse(user) : user;
        return parsed?.email || parsed?.id || "anonymous";
      } catch (e) {
        return "anonymous";
      }
    },

    _getAiLanguage() {
      try {
        const fromI18n = String(i18nService.getActiveLanguage() || "").toLowerCase();
        if (fromI18n) return fromI18n;
      } catch (e) {
      }
      try {
        const fromLs = String(ls.get("language") || "").toLowerCase();
        if (fromLs) return fromLs;
      } catch (e) {
      }
      return "tr";
    },

    _handleAiMessageResponse(data) {
      let message = "";
      if (data?.data?.content) {
        message = data.data.content;
      } else if (data?.data?.message) {
        message = data.data.message;
      } else if (data?.message) {
        message = data.message;
      } else if (data?.content) {
        message = data.content;
      }

      if (!message) {
        message = "Üzgünüm, bir yanıt alınamadı.";
      }

      this.messages.push({
        id: Date.now(),
        sender: "ai",
        content: {
          type: "text",
          message: message,
        },
        timestamp: new Date().toISOString(),
      });
      this.scrollToBottom();
    },
  },

  watch: {
    isOpen(v) {
      if (v) this.scrollToBottom();
    },
    showTypingIndicator(v) {
      if (v) this.scrollToBottom();
    },
    messages: {
      deep: true,
      handler(arr) {
        const last = arr[arr.length - 1];
        if (
            last &&
            last.sender === "ai" &&
            last.content?.type === "interactive" &&
            last.content?.components?.type === "checkbox_list"
        ) {
          this.currentSelections = [];
          this.selectAll = false;
        }
        this.scrollToBottom();
      },
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.anzera-chatbot-container {
  position: relative;
  z-index: 9999;
  display: inline-block;
}

.anzera-chatbot-launcher {
  position: relative;
  display: inline-block;
}

.chatbot-user-avatar {
  width: 32px;
  height: 32px;
  min-width: 32px;
  font-size: 11px;
  flex-shrink: 0;
}

.chatbot-button {
  width: 40px;
  height: 40px;
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  filter: drop-shadow(0 10px 26px rgba(0, 0, 0, 0.15));
  z-index: 1;
}

.chatbot-button:active {
  cursor: pointer;
}

.chatbot-button:focus,
.chatbot-button:focus-visible {
  outline: none;
}

.chatbot-button-img {
  width: 100%;
  height: 100%;
  display: block;
}

@media (max-width: 480px) {
  .chatbot-button {
    width: 32px;
    height: 32px;
  }
}

.chatbot-hello {
  position: absolute;
  top: 50px;
  right: -4px;
  width: 280px;
  max-width: 280px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #e0fcfc;
  border: 2px solid #68ccd1;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  padding: 12px 14px 12px 14px;
}

.chatbot-hello::before,
.chatbot-hello::after {
  content: "";
  position: absolute;
  top: -8px;
  right: 18px;
  border-style: solid;
}

.chatbot-hello::before {
  border-width: 0 9px 9px 9px;
  border-color: transparent transparent #68ccd1 transparent;
}

.chatbot-hello::after {
  top: -6px;
  border-width: 0 8px 8px 8px;
  border-color: transparent transparent #e0fcfc transparent;
}

.chatbot-hello-body {
  min-width: 0;
}

.chatbot-hello-text {
  font-size: 14px;
  line-height: 1.4;
  color: #043d6c;
  margin: 0;
  min-width: 0;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: break-word;
}

.chatbot-cta {
  border: none;
  background: #01acd2;
  color: #fff;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
}

.chatbot-x {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #01acd2;
  background: #e0fcfc;
  color: #01acd2;
  font-size: 14px;
  line-height: 16px;
  cursor: pointer;
  position: absolute;
  top: 8px;
  right: 8px;
  text-align: center;
  padding: 0;
}

.chatbot-panel {
  width: 350px;
  height: 480px;
  position: fixed;
  right: 8rem;
  bottom: 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 9999;
}
.chatbot-header-container{
  width: 100%;
  background-color: #01acd2;
  border-bottom: 1px solid #f1f5f9;
}

.chatbot-header {
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 10px 12px;
}

.chatbot-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chatbot-head {
  width: 50px;
  height: 50px;
}

.chatbot-name {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.chatbot-name-text {
  font-size: 16px;
  margin: 0;
  font-weight: 800;
  color: #0b2150;
}

.chatbot-mode-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  padding: 0px 12px 10px 12px;
  position: relative;
  z-index: 10;
  pointer-events: auto;
}

.mode-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.15);
  color: #fff !important;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  position: relative;
  z-index: 11;
  pointer-events: auto;
}

.mode-btn i {
  font-size: 18px;
  line-height: 1;
  pointer-events: none;
}

.mode-btn:focus {
  outline: none;
  box-shadow: none;
}

.mode-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.8);
  transform: scale(1.05);
}

.mode-btn.active {
  background: #fff;
  border-color: #fff;
  color: #01acd2;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.chatbot-close {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: transparent;
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  outline: none;
}

.chatbot-body {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
}

.msg {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 10px;
  max-width: 100%;
}

.msg.bot {
  justify-content: flex-start;
}

.msg.user {
  justify-content: flex-end;
}

.msg-typing {
  align-items: center;
}

.bot-bubble {
  background: #e0fcfc;
  border: 1px solid #c3f2f2;
  color: #043d6c;
  padding: 8px 10px;
  border-radius: 0 12px 12px 12px;
  max-width: 78%;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.typing-bubble {
  min-width: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  position: static;
}

.typing-dot {
  display: block;
  position: static !important;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #01acd2 !important;
  border: none;
  opacity: 0.35;
  animation: typing-dot 1s infinite ease-in-out;
}

.typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing-dot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  40% {
    transform: translateY(-3px);
    opacity: 1;
  }
}

.ai-message-text {
  color: #043d6c;
}

.user-bubble,
.msg-user-text {
  background: #efefef;
  color: #043d6c;
  padding: 8px 10px;
  border-radius: 12px 0 12px 12px;
  max-width: 78%;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.quick {
  display: flex;
  flex-direction: column;
}

.quick button {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #043d6c;
  border-radius: 5px;
  padding: 6px 10px;
  margin: 2px;
  font-size: 12px;
  text-align: left;
  cursor: pointer;
}

.details-container {
  background-color: #e9ecef;
  border-left: 3px solid #01acd2;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  font-size: 0.9em;
  padding: 2px 0;
}

.action-buttons-container {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-button {
  padding: 8px 12px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  flex: 1 1 auto;
  min-width: 80px;
  transition: background-color 0.2s;
}

.checkbox-list-container {
  margin-top: 10px;
  border: 1px solid #ccc;
  padding: 10px;
  border-radius: 5px;
  max-height: 25vh;
  overflow: auto;
}

.checkbox-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  padding: 5px 0;
  border-bottom: 1px dotted #e0e0e0;
}

.checkbox-item input {
  width: 1rem !important;
  height: 1rem !important;
}

.checkbox-item:last-child {
  border-bottom: none;
}

.checkbox-item label {
  font-size: 14px !important;
  margin-left: 8px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.checkbox-item small {
  color: #777;
  font-size: 0.8em;
}

.send-selection-button {
  width: 100%;
  background-color: #01acd2;
  color: white;
}

.button-group-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.chatbot-input {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px 18px;
  border-top: 1px solid #f1f5f9;
}

.chatbot-input::after {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 8px;
  height: 2px;
  background: #cfd8dc;
  border-radius: 2px;
}

.chatbot-editor {
  flex: 1;
  min-height: 20px;
  max-height: 80px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
  box-shadow: none;
  background: transparent;
  padding: 0;
  margin: 0 6px;
  font-size: 14px;
  color: #0f172a;
  caret-color: #01acd2;
}

.chatbot-editor-input {
  min-height: unset;
  max-height: unset;
  height: 36px;
  line-height: 36px;
  color: #043d6c;
  border: none;
}

.chatbot-editor::placeholder {
  color: #9aa8b3;
}

.chatbot-send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  outline: none;
}

.chatbot-send-btn i {
  font-size: 22px;
  color: #01acd2 !important;
}

.chatbot-send-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.chatbot-send-btn:disabled i {
  color: #9ccbd6;
}

.chatbot-presence .point.on {
  background: #22c55e !important;
}

.chatbot-presence .point.off {
  background: #fc0606 !important;
}

.action-disabled,
.quick:has(button[disabled]) {
  opacity: 0.6;
  pointer-events: none;
  filter: grayscale(0.05);
}

@media (max-width: 640px) {
  .chatbot-panel {
    left: 12px;
    right: auto;
    bottom: 1.5rem;
    width: min(calc(100vw - 24px), 350px);
    height: 60vh;
  }

  .chatbot-hello {
    width: min(calc(100vw - 24px), 320px);
  }
}

@media (max-width: 480px) {
  .chatbot-panel {
    width: min(calc(100vw - 24px), 340px);
    height: 55vh;
  }

  /* hello balonu soldaki alanın altında kalmasın: viewport'a sabitlenir */
  .chatbot-hello {
    position: fixed !important;
    left: 12px !important;
    right: 12px !important;
    top: 60px !important;
    bottom: auto !important;
    width: auto !important;
    max-width: none !important;
  }
  .chatbot-hello::before,
  .chatbot-hello::after {
    display: none; /* ok işareti sabit konumda anlamsız */
  }
}

.chatbot-panel .chatbot-presence span:not(.point) {
  position: static !important;
  right: auto !important;
  top: auto !important;
  margin: 0 !important;
  padding: 0 !important;
  width: auto !important;
  height: auto !important;
  background: transparent !important;
  border-radius: 0 !important;
  display: inline-block !important;
  color: #fff !important;
  font-size: 13px !important;
  line-height: 1.2 !important;
}

.chatbot-panel .chatbot-presence .point {
  position: static !important;
  width: 8px !important;
  height: 8px !important;
  margin: 0 4px 0 0 !important;
  border-radius: 50% !important;
  display: inline-block !important;
}

.action-buttons-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  margin-top: 10px;
}

.cancel-selection-button {
  flex: 1;
  background-color: #ef4444;
  color: white;
}

.cancel-selection-button:hover:not(:disabled) {
  background-color: #dc2626;
}
</style>

<!-- Panel container body'ye taşındığında stiller -->
<style>
/* Teleport container - body'de görünmez wrapper */
.chatbot-panel-teleport {
  display: contents;
}

/* Panel body'deki teleport container içinde */
body > .chatbot-panel-teleport .chatbot-panel {
  width: 350px;
  height: 480px;
  position: fixed;
  right: 8rem;
  bottom: 1.5rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  z-index: 99999;
}

@media (max-width: 640px) {
  body > .chatbot-panel-teleport .chatbot-panel {
    left: 12px;
    right: auto;
    bottom: 1.5rem;
    width: min(calc(100vw - 24px), 350px);
    height: 60vh;
  }
}

@media (max-width: 480px) {
  body > .chatbot-panel-teleport .chatbot-panel {
    width: min(calc(100vw - 24px), 340px);
    height: 75vh;
  }
}
</style>
